import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, Phone, MapPin, Loader2 } from "lucide-react";

// List of Indian states
const indianStates = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

export default function UserDetails() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    phone: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    country: "India",
    otherCountry: "",
  });
  const [existingData, setExistingData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  const [apiError, setApiError] = useState("");

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Get email from Auth0 data in localStorage
  const [userEmail, setUserEmail] = useState("");
  
  useEffect(() => {
    // Extract email from localStorage Auth0 data
    try {
      const auth0DataKeys = Object.keys(localStorage).filter(key => 
        key.startsWith("@@auth0spajs@@")
      );
      
      if (auth0DataKeys.length > 0) {
        const auth0Key = auth0DataKeys.find(key => key.includes("@@user@@"));
        if (auth0Key) {
          const userData = JSON.parse(localStorage.getItem(auth0Key));
          if (userData && userData.decodedToken && userData.decodedToken.claims && userData.decodedToken.claims.email) {
            setUserEmail(userData.decodedToken.claims.email);
          }
        }
      }
    } catch (error) {
      console.error("Error getting email from localStorage:", error);
    }
  }, []);
  
  // Track which fields are already completed
  const [formFieldsCompleted, setFormFieldsCompleted] = useState({
    phone: false,
    address: false
  });
  
  // Fetch user data when userEmail is available
  useEffect(() => {
    const fetchUserData = async () => {
      if (!userEmail) return;
      
      setLoadingUser(true);
      try {
        const response = await fetch(`${API_BASE_URL}/get-user-profile/${encodeURIComponent(userEmail)}/`);
        if (!response.ok) {
          throw new Error("Failed to fetch user data");
        }
        
        const data = await response.json();
        setExistingData(data);
        
        // Pre-fill form with existing data that is editable
        const updatedFormData = { ...formData };
        
        if (data.phone) {
          updatedFormData.phone = data.phone;
          setFormFieldsCompleted(prev => ({...prev, phone: true}));
        }
        
        if (data.address) {
          updatedFormData.street = data.address.street || "";
          updatedFormData.city = data.address.city || "";
          updatedFormData.state = data.address.state || "";
          updatedFormData.zip = data.address.zip || "";
          updatedFormData.country = data.address.country || "India";
          setFormFieldsCompleted(prev => ({...prev, address: true}));
        }
        
        setFormData(updatedFormData);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setApiError("Unable to load your profile. Please try again later.");
      } finally {
        setLoadingUser(false);
      }
    };
    
    if (userEmail) {
      fetchUserData();
    }
  }, [userEmail]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Only validate fields that will be sent to the backend
    // (fields that don't already exist in the user's data)
    if (!formFieldsCompleted.phone) {
      if (!formData.phone)
        newErrors.phone = "Phone number is required";
      else if (!/^\d{10}$/.test(formData.phone))
        newErrors.phone = "Phone number must be 10 digits";
    }
    
    // Only validate address fields if address doesn't already exist
    if (!formFieldsCompleted.address) {
      if (!formData.street) newErrors.street = "Street address is required";
      if (!formData.city) newErrors.city = "City is required";
      if (!formData.state) newErrors.state = "State is required";
      if (!formData.zip) newErrors.zip = "ZIP code is required";
      else if (!/^\d{5,6}$/.test(formData.zip))
        newErrors.zip = "ZIP code must be 5 or 6 digits";
      if (!formData.country) newErrors.country = "Country is required";
      if (formData.country === "Others" && !formData.otherCountry)
        newErrors.otherCountry = "Please specify your country";
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setLoading(true);
    setApiError("");
    
    try {
      // Format data to match backend structure
      const countryValue = formData.country === "Others" ? formData.otherCountry : formData.country;
      
      // Only include fields in the payload that don't already exist in the user's data
      const payload = {};
      
      if (!formFieldsCompleted.phone) {
        payload.phone = formData.phone;
      }
      
      // Only add address if it doesn't already exist
      if (!formFieldsCompleted.address) {
        payload.address = {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          country: countryValue,
        };
      }

      const response = await fetch(`${API_BASE_URL}/update-user/${encodeURIComponent(userEmail)}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json', 
        },
        body: JSON.stringify(payload),
      });
      
      // Check if response is successful first
      if (response.ok) {
        // Try to parse JSON, but handle cases where it might not be JSON
        let data;
        try {
          const responseText = await response.text();
          data = responseText ? JSON.parse(responseText) : {};
        } catch (parseError) {
          console.warn("Response is not valid JSON, but request was successful");
          data = { success: true };
        }
        
        console.log("User details saved:", data);
        
        // Update completed fields
        if (payload.phone) setFormFieldsCompleted(prev => ({...prev, phone: true}));
        if (payload.address) setFormFieldsCompleted(prev => ({...prev, address: true}));
        
        // Show success message or redirect
        navigate("/"); // Redirect to homepage on success
        
      } else {
        // Handle non-200 responses
        let errorMessage = "Failed to update user details";
        try {
          const responseText = await response.text();
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          // If we can't parse error response, use generic message
          errorMessage = `Server error (${response.status}): ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
      
    } catch (error) {
      console.error("API Error:", error);
      setApiError(error.message || "Failed to save details. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // Check if profile is already complete
  const isProfileComplete = formFieldsCompleted.phone && formFieldsCompleted.address;

  return (
    <>
      {/* Inline CSS for animations */}
      <style>
        {`
          @keyframes fadeInUp {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeInUp {
            animation: fadeInUp 1s ease-out forwards;
          }
        `}
      </style>
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white py-12 px-4 md:px-8">
        <div className="max-w-lg w-full bg-white rounded-sm shadow-lg p-8 animate-fadeInUp">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl text-[#333333] mb-2 tracking-wide">
              Complete Your Profile
            </h2>
            <p className="text-sm text-gray-500">
              Hi {existingData.name || existingData.first_name || "there"}, please provide your contact details to enhance your shopping experience.
            </p>
            <div className="mt-4 h-1 w-20 bg-[#D4AF37] mx-auto rounded-full"></div>
          </div>
          
          {/* Error Message */}
          {apiError && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-sm text-sm">
              {apiError}
            </div>
          )}
          
          {/* Loading State - Show when userEmail is not yet available */}
          {!userEmail ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-[#D4AF37] animate-spin mb-4" />
              <p className="text-gray-600">Loading your account information...</p>
            </div>
          ) : loadingUser ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-[#D4AF37] animate-spin mb-4" />
              <p className="text-gray-600">Loading your profile information...</p>
            </div>
          ) : isProfileComplete ? (
            <div className="bg-green-50 border border-green-200 rounded-sm p-6 text-center">
              <div className="text-green-600 font-medium text-lg mb-2">Your profile is complete!</div>
              <p className="text-gray-600 mb-4">Thank you for providing all the required information.</p>
              <button 
                onClick={() => navigate("/")}
                className="bg-[#D4AF37] text-black py-2 px-6 rounded-sm text-sm font-medium hover:bg-[#C19B2E] transition-all duration-200"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* User Email (Display Only) */}
              <div>
                <label className="flex items-center gap-2 text-sm text-[#333333] mb-2">
                  <User className="h-4 w-4 text-[#D4AF37]" />
                  Email
                </label>
                <input
                  type="email"
                  value={userEmail}
                  className="w-full p-3 border border-gray-300 rounded-sm bg-gray-50 cursor-not-allowed"
                  disabled
                />
                <p className="mt-1 text-xs text-gray-500">Your account email (cannot be changed)</p>
              </div>
              
              {/* Phone Number */}
              {!formFieldsCompleted.phone && (
                <div>
                  <label className="flex items-center gap-2 text-sm text-[#333333] mb-2">
                    <Phone className="h-4 w-4 text-[#D4AF37]" />
                    Phone Number
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full p-3 border rounded-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                      errors.phone ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter your phone number (10 digits)"
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.phone}
                    </p>
                  )}
                </div>
              )}
              
              {/* Shipping Address */}
              {!formFieldsCompleted.address && (
                <div>
                  <label className="flex items-center gap-2 text-sm text-[#333333] mb-2">
                    <MapPin className="h-4 w-4 text-[#D4AF37]" />
                    Shipping Address
                  </label>
                  <div className="space-y-4">
                    {/* Street */}
                    <input
                      type="text"
                      name="street"
                      value={formData.street}
                      onChange={handleChange}
                      className={`w-full p-3 border rounded-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                        errors.street ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Street address"
                    />
                    {errors.street && (
                      <p className="mt-1 text-sm text-red-500">{errors.street}</p>
                    )}
                    
                    {/* City */}
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className={`w-full p-3 border rounded-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                        errors.city ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="City"
                    />
                    {errors.city && (
                      <p className="mt-1 text-sm text-red-500">{errors.city}</p>
                    )}
                    
                    {/* State */}
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className={`w-full p-3 border rounded-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                        errors.state ? "border-red-500" : "border-gray-300"
                      }`}
                    >
                      <option value="">Select State</option>
                      {indianStates.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                    {errors.state && (
                      <p className="mt-1 text-sm text-red-500">{errors.state}</p>
                    )}
                    
                    {/* ZIP Code */}
                    <input
                      type="text"
                      name="zip"
                      value={formData.zip}
                      onChange={handleChange}
                      className={`w-full p-3 border rounded-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                        errors.zip ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="ZIP Code"
                    />
                    {errors.zip && (
                      <p className="mt-1 text-sm text-red-500">{errors.zip}</p>
                    )}
                    
                    {/* Country */}
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className={`w-full p-3 border rounded-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                        errors.country ? "border-red-500" : "border-gray-300"
                      }`}
                    >
                      <option value="India">India</option>
                      <option value="Others">Others</option>
                    </select>
                    {formData.country === "Others" && (
                      <input
                        type="text"
                        name="otherCountry"
                        value={formData.otherCountry}
                        onChange={handleChange}
                        className={`w-full p-3 border rounded-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                          errors.otherCountry ? "border-red-500" : "border-gray-300"
                        } mt-2`}
                        placeholder="Enter your country"
                      />
                    )}
                    {errors.country && (
                      <p className="mt-1 text-sm text-red-500">{errors.country}</p>
                    )}
                    {errors.otherCountry && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.otherCountry}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* What's missing section */}
              <div className="bg-yellow-50 p-4 rounded-sm border border-yellow-200">
                <h3 className="text-sm font-medium text-yellow-800 mb-2">Information needed to complete your profile:</h3>
                <ul className="text-sm space-y-1">
                  {!formFieldsCompleted.phone && (
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                      Phone number
                    </li>
                  )}
                  {!formFieldsCompleted.address && (
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                      Shipping address
                    </li>
                  )}
                </ul>
              </div>
              
              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || (formFieldsCompleted.phone && formFieldsCompleted.address)}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#C19B2E] text-black py-3 rounded-sm text-sm font-medium hover:from-[#C19B2E] hover:to-[#B08927] transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg flex items-center justify-center"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  "Save Details"
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}